import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Sunrise, ChevronDown, ArrowUpRight } from 'lucide-react'
import { useMe } from '../../lib/me.js'
import { canManage } from '../../lib/role.js'
import { apiGet } from '../../lib/api.js'
import { firstHuddle, filledGoals, goalCat, goalTarget } from '../../lib/huddle.js'
import { Loading, fmtDateText } from '../../kit'

/**
 * Today's morning huddle, read-only, collapsed into a strip above the
 * appointment calendar — the goals the team agreed at 08:30 in front of whoever
 * is working the schedule, without a trip to /huddle.
 *
 * Scope follows the calendar: same branch, and the same day while the calendar
 * is on a day (in month view there is no meaningful day, so it falls back to
 * today). The date is always printed in the header, so which huddle this is can
 * never be ambiguous.
 *
 * The goals and ประกาศ are always on screen — they are the reason the strip
 * exists, and a target nobody can see is not a target. Only บันทึกตามหัวข้อ
 * folds away, at the bottom, since it is the long-form half; that choice is
 * remembered per browser. Fails quiet: a widget on the schedule must never
 * block the schedule.
 */
const NOTES_KEY = 'ss.appt.huddle.notes'

const readNotesOpen = () => {
  try { return localStorage.getItem(NOTES_KEY) !== '0' } catch { return true }
}
const writeNotesOpen = (open) => {
  try { localStorage.setItem(NOTES_KEY, open ? '1' : '0') } catch { /* private mode — not worth throwing over */ }
}

export default function HuddlePanel({ branchId, date }) {
  const me = useMe()
  const manager = canManage(me)
  const [notesOpen, setNotesOpen] = useState(readNotesOpen)
  const [huddle, setHuddle] = useState(null)
  const [loading, setLoading] = useState(true)
  const scope = `${branchId}|${date}`
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  useEffect(() => {
    if (!branchId || !date) return
    const params = { branch_id: branchId, huddle_date: date, per_page: 1 }
    if (!manager) params.status = 'published'   // staff never see an unpublished row
    setLoading(true)
    apiGet('/api/huddle', params)
      .then((r) => { if (alive.current) { setHuddle(firstHuddle(r)); setLoading(false) } })
      .catch(() => { if (alive.current) { setHuddle(null); setLoading(false) } })
  }, [scope, manager]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleNotes = () => setNotesOpen((o) => { writeNotesOpen(!o); return !o })

  const goals = filledGoals(huddle?.daily_goals)
  const announcements = (huddle?.announcements || '').trim()
  const notes = Array.isArray(huddle?.notes) ? huddle.notes.filter((n) => (n.body || '').trim()) : []
  const empty = !huddle || (goals.length === 0 && !announcements && notes.length === 0)

  return (
    <div className="mb-4 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center gap-2.5 px-4 py-2.5">
        <span className="w-7 h-7 rounded-lg bg-amber-50 text-amber-500 flex items-center justify-center flex-none">
          <Sunrise size={16} strokeWidth={1.9} />
        </span>
        <span className="text-[14px] font-semibold text-slate-800">ประชุมเช้า</span>
        <span className="text-[12.5px] text-slate-400 truncate">{fmtDateText(date, { weekday: true })}</span>
        <Link
          to="/huddle"
          className="ml-auto flex-none inline-flex items-center gap-1 text-[12.5px] font-medium text-brand-700 hover:text-brand-800"
        >
          เปิดหน้าประชุม <ArrowUpRight size={13} />
        </Link>
      </div>

      <div className="px-4 pb-3.5 border-t border-slate-100">
        {loading ? (
          <div className="py-2.5"><Loading variant="inline" /></div>
        ) : empty ? (
          <div className="py-2.5 text-[12.5px] text-slate-400">
            {huddle ? 'การประชุมนี้ยังไม่มีเนื้อหา'
              : manager ? 'ยังไม่มีการประชุมของวันนี้ — เปิดหน้าประชุมเพื่อสร้าง'
              : 'ยังไม่มีการประชุมเช้าที่เผยแพร่'}
          </div>
        ) : (
          <>
            {/* เป้าหมาย — never folds away. */}
            {goals.length > 0 && (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 pt-3">
                {goals.map((g, i) => {
                  const c = goalCat(g.category)
                  // Same shape as the /huddle read view: with no detail the
                  // category name IS the goal and moves up to the main line.
                  return (
                    <div key={i} className="flex items-start gap-2 rounded-xl bg-slate-50 ring-1 ring-slate-100 px-2.5 py-2">
                      <span className="text-[15px] leading-none mt-0.5">{c.icon}</span>
                      <div className="min-w-0 flex-1">
                        {(g.text || '').trim() ? (
                          <>
                            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{c.label}</div>
                            <div className="text-[13px] text-slate-800 leading-snug whitespace-pre-wrap">{g.text}</div>
                          </>
                        ) : (
                          <div className="text-[13px] text-slate-800 leading-snug">{c.label}</div>
                        )}
                      </div>
                      {g.target && <span className="flex-none self-start mt-0.5 text-[12px] font-semibold text-brand-700 bg-brand-50 rounded-md px-1.5 py-0.5 tabular-nums">{goalTarget(g.target)}</span>}
                    </div>
                  )
                })}
              </div>
            )}

            {/* ประกาศ — an announcement nobody sees is not an announcement. */}
            {announcements && (
              <div className="pt-3">
                <div className="text-[12.5px] font-semibold text-slate-700 mb-0.5">📢 ประกาศ</div>
                <div className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap">{announcements}</div>
              </div>
            )}

            {/* บันทึกตามหัวข้อ — the long-form half, last and foldable. */}
            {notes.length > 0 && (
              <div className="mt-3 pt-2.5 border-t border-slate-100">
                <button
                  type="button" onClick={toggleNotes} aria-expanded={notesOpen}
                  className="flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-600 hover:text-brand-700 transition-colors"
                >
                  <ChevronDown size={14} className={`transition-transform ${notesOpen ? '' : '-rotate-90'}`} />
                  📝 บันทึกตามหัวข้อ
                  <span className="font-medium text-slate-400">({notes.length})</span>
                </button>
                {notesOpen && (
                  <div className="mt-2 space-y-2">
                    {notes.map((n, i) => (
                      <div key={i} className="border-l-2 border-brand-200 pl-2.5">
                        {(n.topic || '').trim() && <div className="text-[12px] font-semibold text-slate-700">{n.topic}</div>}
                        <div className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap">{n.body}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
